import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Generic search hook with debounce and abort support
 * @param {Function} searchFn - Async function that performs the search (query, nextUrl, signal) => { items, next, error }
 * @param {number} debounceMs - Debounce delay in milliseconds
 */
export function useSearch(searchFn, { debounceMs = 300 } = {}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const search = useCallback((q) => {
    setQuery(q);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      setNextUrl(null);
      return;
    }
    setLoading(true);
    timeoutRef.current = setTimeout(async () => {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      try {
        const response = await searchFn(q, null, abortController.signal);
        if (response.error) {
          console.error('Search error:', response.error);
          setResults([]);
          setNextUrl(null);
        } else {
          setResults(response.items || []);
          setNextUrl(response.next || null);
        }
      } catch {
        setResults([]);
        setNextUrl(null);
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }, debounceMs);
  }, [searchFn, debounceMs]);

  const loadMore = useCallback(async () => {
    if (!nextUrl || loadingMore || !query.trim()) {
      return;
    }

    setLoadingMore(true);
    try {
      const response = await searchFn(query, nextUrl, null);
      if (response.error) {
        console.error('Load more error:', response.error);
        setNextUrl(null);
      } else {
        setResults(prev => [...prev, ...(response.items || [])]);
        setNextUrl(response.next || null);
      }
    } catch (error) {
      console.error('Load more failed:', error);
      setNextUrl(null);
    } finally {
      setLoadingMore(false);
    }
  }, [searchFn, query, nextUrl, loadingMore]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Memoize the returned object to prevent recreating on every render
  // This prevents dependent effects from re-running unnecessarily
  return useMemo(() => ({ 
    query, 
    setQuery: search, 
    results, 
    loading, 
    loadingMore, 
    hasMore: !!nextUrl, 
    loadMore 
  }), [query, search, results, loading, loadingMore, nextUrl, loadMore]);
}
